import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/notificationTable.css';
import { getNotificationsApi, subscribeNotificationSSE, ApiNotificationItem, ApiNotificationMessage } from './notificationApi';
import { NotificationItem } from './notificationData';
import EmptyNotification from '../../empty-notification/ts/emptyNotification';

/* ── Extended local type ─────────────────────────────────── */
interface NotifRow extends NotificationItem {
  module: string;
}

/* ── Helpers ─────────────────────────────────────────────── */

function formatDate(raw: string): string {
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day:   '2-digit',
      month: 'short',
      year:  'numeric',
    });
  } catch {
    /* istanbul ignore next */
    return raw;
  }
}

function parseMessage(raw: string): { module: string; message: string } {
  try {
    return JSON.parse(raw.trim());
  } catch {
    return { module: '', message: raw };
  }
}

const MODULE_ROUTES: Record<string, string> = {
  workflow: '/workflow',
  media:    '/media',
  career:   '/career/hire',
  inbox:    '/inbox',
  setting: '/setting',
  content: 'content'
};

function getModuleRoute(module: string): string {
  return MODULE_ROUTES[module.toLowerCase()] ?? '/';
}

function mapApiItem(item: ApiNotificationItem): NotificationItem {
  const parsed: ApiNotificationMessage =
    typeof item.message === 'object' &&  /* istanbul ignore next */ item.message !== null
      ?  /* istanbul ignore next */ item.message as ApiNotificationMessage //NOSONAR
      : parseMessage(String(item.message ??  /* istanbul ignore next */ ''));

  return {
    id:          String(item.id),
    date:        formatDate(item.createdAt),
    title:       parsed.module  ??  /* istanbul ignore next */ '',
    description: parsed.message ?? /* istanbul ignore next */ '',
    module:      parsed.module  ??  /* istanbul ignore next */'',
  };
}
function extractItems(res: any): ApiNotificationItem[] {
  const d = res?.data;
  if (Array.isArray(d))          return d;
  if (Array.isArray(d?.data))    return d.data;
  if (Array.isArray(d?.items))   return d.items;
  /* istanbul ignore next */
  if (Array.isArray(res?.items)) return res.items;
  return [];
}

/* ── Types ───────────────────────────────────────────────── */

export interface NotificationTableProps {
  title?:       string;
  seeMoreHref?: string;
  onSeeMore?:   () => void;
  onOpenClick?: () => void;
}

/* ── Component ───────────────────────────────────────────── */

const NotificationTable: React.FC<NotificationTableProps> = ({
  title       = 'Notification',
  seeMoreHref = '#',
  onSeeMore,
  onOpenClick,
}) => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<NotifRow[]>([]);
  const [loading,       setLoading]       = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res   = await getNotificationsApi();
      const items = extractItems(res).map(mapApiItem) as NotifRow[];
      setNotifications(items);
    } catch (e) { //NOSONAR
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // ── Realtime SSE ───────────────────────────────────────────
  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const unsubscribe = subscribeNotificationSSE((item: ApiNotificationItem) => {
      const mapped = mapApiItem(item) as NotifRow;

      const t = setTimeout(() => {
        setNotifications((prev) => { //NOSONAR
          if (prev.some((n) => n.id === mapped.id)) return prev;
          return [mapped, ...prev].slice(0, 6);
        });
      }, 5000);

      timeouts.push(t);
    });

    return () => {
      unsubscribe();
      timeouts.forEach(clearTimeout);
    };
  }, []);
  return (
    <div className="scl--cms-components-notifTable-card">

      {/* ── Header ── */}
      <div className="scl--cms-components-notifTable-header">
        <button className="scl--cms-components-notifTable-title" onClick={onOpenClick}>
          {title}
        </button>

        <a
          href={seeMoreHref}
          className="scl--cms-components-notifTable-seeMore"
          onClick={onSeeMore ? (e) => { e.preventDefault(); onSeeMore(); } : undefined}
        >
          See more
        </a>
      </div>

      {/* ── Column headers ── */}
      <div className="scl--cms-components-notifTable-thead">
        <span className="scl--cms-components-notifTable-th scl--cms-components-notifTable-th--date">
          Date
        </span>
        <span className="scl--cms-components-notifTable-th">
          Description
        </span>
      </div>

      {/* ── Rows ── */}
      <div className="scl--cms-components-notifTable-body">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="scl--cms-components-notifTable-row" //NOSONAR
            >

              {/* Date cell */}
              <div className="scl--cms-components-notifTable-cell scl--cms-components-notifTable-cell--date">
                <div className="scl--cms-components-notifTable-skeleton scl--cms-components-notifTable-skeleton--date" />
              </div>

              {/* Description cell */}
              <div className="scl--cms-components-notifTable-cell scl--cms-components-notifTable-cell--desc">
                <div className="scl--cms-components-notifTable-text">
                  <div
                    className="scl--cms-components-notifTable-skeleton scl--cms-components-notifTable-skeleton--row-title"
                    style={{ width: `${60 + (i % 3) * 20}px` }}
                  />
                  <div
                    className="scl--cms-components-notifTable-skeleton scl--cms-components-notifTable-skeleton--row-desc"
                    style={{ width: `${180 + (i % 4) * 30}px` }}
                  />
                </div>
              </div>

            </div>
          ))
        ) : notifications.length === 0 ? ( //NOSONAR
            <EmptyNotification />
        ) : (
          notifications.slice(0, 6).map((n) => (
            <div key={n.id} className="scl--cms-components-notifTable-row">

              {/* Date cell */}
              <div className="scl--cms-components-notifTable-cell scl--cms-components-notifTable-cell--date">
                <span className="scl--cms-components-notifTable-date">{n.date}</span>
              </div>

              {/* Description cell */}
              <div className="scl--cms-components-notifTable-cell scl--cms-components-notifTable-cell--desc">
                <div className="scl--cms-components-notifTable-text">
                  <button
                    className="scl--cms-components-notifTable-notifTitle"
                    onClick={() => navigate(getModuleRoute(n.module))}
                  >
                    {n.title}
                  </button>
                  <span className="scl--cms-components-notifTable-notifDesc">{n.description}</span>
                </div>
              </div>

            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default NotificationTable;